<template>
  <div class="document-view">
    <panel>
      <div class="card-body details-card">
        <div v-if="!withoutInfo && isSubmittedManually && !isDelivered" class="float-right">
          <p v-if="submittedManuallyText" v-html="submittedManuallyText" />

          <p v-else-if="submitToSchool">
            Wniosek został zapisany w systemie. Pobierz wniosek, wydrukuj, podpisz i dostarcz do szkoły, do której
            uczęszcza uczeń
          </p>

          <p v-else>
            Pobierz wniosek, wydrukuj, podpisz i dostarcz do Wydziału Oświaty i Wychowania Urzędu Miasta Lublin, ul.
            Gabriela Narutowicza 37/39.
          </p>
        </div>

        <check-permission
          v-if="!document.procedure.hidden"
          permissions="DOCUMENT_VIEW.PROCEDURE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Procedura:
            <strong>{{ document.procedure.name }}</strong>
          </p>
        </check-permission>

        <check-permission permissions="DOCUMENT_VIEW.ID" :entity-type="entityType" :entity-id="document.id.toString()">
          <p>
            Identyfikator:
            <strong>{{ document.id }}</strong>
          </p>
        </check-permission>

        <p v-if="document.systemNumbers && document.systemNumbers.mdok">
          Numer MDOK:
          <strong>{{ document.systemNumbers.mdok }}</strong>
        </p>

        <p v-if="document.systemNumbers && document.systemNumbers.sign">
          Znak sprawy:
          <strong>{{ document.systemNumbers.sign }}</strong>
        </p>

        <check-permission
          permissions="DOCUMENT_VIEW.STATUS"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Status:
            <strong>{{ document.status.name }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="isDelivered && documentIndex"
          permissions="DOCUMENT_VIEW.SUBMIT_DATE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Data złożenia:
            <strong>{{ documentIndex.values.DELIVERY_DATE | moment('DD-MM-YYYY') }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="!separateSchoolsDisplay && documentIndex && documentIndex.data && documentIndex.data.school"
          permissions="DOCUMENT_VIEW.SCHOOL_NAME"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Szkoła:
            <strong> {{ documentIndex.data.school.schoolName }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-else-if="separateSchoolsDisplay && documentIndex && documentIndex.data && documentIndex.data.school"
          permissions="DOCUMENT_VIEW.SCHOOL_NAME"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <div>
            <p>
              Szkoła, z której Wnioskodawca chce duplikat:
              <strong> {{ documentIndex.data.school.selectedSchoolName }}</strong>
            </p>

            <p>
              Szkoła, do której składany jest wniosek:
              <strong> {{ documentIndex.data.school.schoolName }}</strong>
            </p>
          </div>
        </check-permission>

        <check-permission
          v-if="studentFullName"
          permissions="DOCUMENT_VIEW.TENDERER"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Uczeń:
            <strong>
              {{ studentFullName }}
            </strong>
          </p>
        </check-permission>

        <check-permission
          v-if="applicantOrTenderer"
          permissions="DOCUMENT_VIEW.APPLICANT"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Wnioskodawca:
            <strong>
              {{ applicantOrTenderer }}
            </strong>
          </p>
        </check-permission>

        <check-permission
          permissions="DOCUMENT_VIEW.CHECKSUM"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Suma kontrolna:
            <strong>{{ document.checksum }}</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="!parentId && !withoutInfo"
          permissions="DOCUMENT_VIEW.SIGNATURE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Podpis elektroniczny:
            <strong>{{ signature(document.signedAttachment) }}</strong>
          </p>
        </check-permission>

        <p v-if="document.data && document.data.termPassedReason">
          Powód złożenia po terminie:
          <strong>{{ document.data.termPassedReason }}</strong>
        </p>

        <check-permission
          v-if="!parentId && !withoutInfo && isSubmitted && documentIndex && documentIndex.values"
          permissions="DOCUMENT_VIEW.RESPONSE_TYPE"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <p>
            Forma odpowiedzi:
            <strong v-if="documentIndex.values.REPLY_BY_EPUAP">
              Elektronicznie, na skrzynkę: {{ documentIndex.values.EPUAP_ADDRESS }}
            </strong>

            <strong v-else>Papierowo</strong>
          </p>
        </check-permission>

        <check-permission
          v-if="!parentId && !withoutInfo && documentIndex && documentIndex.currentDecision"
          permissions="DOCUMENT_VIEW.DECISION"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <decision-data :document-index="documentIndex" />
        </check-permission>

        <check-permission
          v-if="
            !parentId &&
              documentIndex &&
              !!documentIndex.signedAttachmentId &&
              (!documentIndex.values || !documentIndex.values.DELIVERY_DATE)
          "
          permissions="DOCUMENT.SUBMIT_EPUAP"
          :entity-type="entityType"
          :entity-id="document.id.toString()"
        >
          <div>
            <p>
              Kliknij "Wyślij przez ePUAP", aby przejść do widoku wysyłania pisma w ePUAP lub pobierz podpisany plik XML
              z wnioskiem i wyślij go samodzielnie.
            </p>

            <w-button
              icon="fas fa-envelope mr-2"
              class="btn-light mr-2"
              text="Wyślij przez ePUAP"
              @click="sendWithEpuap"
            />

            <post-form
              icon="fas fa-download mr-2"
              button-class="btn-light mr-2"
              text="Pobierz XML"
              :url="downloadXmlLink"
            />

            <div ref="temporaryFormContainer" class="d-none" />
          </div>
        </check-permission>

        <div v-if="!parentId" class="float-right">
          <check-permission
            v-if="!!mdokUid"
            permissions="DOCUMENT.MDOK"
            entity-type="document"
            :entity-id="document.id"
          >
            <w-button icon="fas fa-link mr-2" class="btn-light mr-2" text="Przejdź do MDOK" @click="goToMdok" />
          </check-permission>

          <w-button icon="fal fa-eye mr-2" class="btn-light mr-2" text="Podgląd" @click="openPreview" />

          <post-form
            v-if="isSubmitted"
            button-class="mr-2 btn-light"
            icon="fal fa-cloud-download mr-2"
            text="Pobierz"
            :url="renderUrl"
          />

          <system-message
            button-class="btn btn-light mr-2 "
            icon="fal fa-fw fa-bell"
            :entity-type="entityType"
            :entity-id="document.id.toString()"
            :name="messageName"
          />

          <check-permission
            v-if="documentIndex.values && documentIndex.values.EPUAP_ADDRESS"
            permissions="DOCUMENT.SET_EPUAP"
            entity-type="document"
            :entity-id="document.id"
          >
            <w-button class="btn-light mr-2" text="Zmień adres skrytki ePUAP" @click="openModal('epuap')" />
          </check-permission>
        </div>

        <p v-if="lock" class="lock-info">
          Wniosek edytowany przez: <strong>{{ lock.userLogin }}</strong>
        </p>
      </div>

      <actions
        slot="footer"
        :entity-type="entityType"
        :actions="actions"
        :object="document"
        @navigate="navigate"
        @execute="execute"
        @setStatus="openStatusPicker($event.object)"
        @preview="getPreview($event)"
      />
    </panel>

    <pick-status-modal
      :id="statusPickerDocumentId"
      :comment-enabled="true"
      :object="statusPickerDocument"
      :show="showStatusPicker"
      @update="saveStatus"
      @close="closeStatusPicker"
    />

    <schema-form-modal
      :object="actionData"
      :show="!!actionData"
      :external="{ documentIndex }"
      @save="sendModalData"
      @close="closeActionDataModal"
    />

    <preview-modal :show="showPreviewModal" :html="previewHTML" @close="closePreviewModal" />

    <epuap-address-modal :show="modals.epuap" :document-index="documentIndex" @close="closeModal('epuap')" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Actions from '@/components/actions/Actions';
import DecisionData from '@/components/document/DecisionData';
import SystemMessage from '@/components/formComponents/SystemMessage';
import EpuapAddressModal from '@/components/modals/EpuapAddressModal.vue';
import PreviewModal from '@/components/modals/PreviewModal';
import PostForm from '@/components/postForm/PostForm';
import { DocumentValueCodes } from '@/enums/documentValueCodes.enum';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { StageType } from '@/enums/stageType.enum';
import { TableName } from '@/enums/tableName';
import { buttonsMixin } from '@/mixins/buttons';
import { displayModalMixin } from '@/mixins/displayModal';
import { documentManageMixin } from '@/mixins/document/documentManage';
import { documentRenderMixin } from '@/mixins/document/documentRender';
import { previewMixin } from '@/mixins/preview';
import { signatureMixin } from '@/mixins/signature';
import { documentResource } from '@/resources/document.resource';
import { epuapResource } from '@/resources/epuap.resource';
import { config } from '@/utils/config';
import { SET_STATUS_ACTION } from '@/utils/constants';
import { getDocumentViewData } from '@/utils/document';

// @vuese
// Widok dokumentu
// @group Document
export default {
  components: { Actions, DecisionData, EpuapAddressModal, PostForm, PreviewModal, SystemMessage },
  mixins: [
    displayModalMixin,
    documentManageMixin,
    documentRenderMixin,
    buttonsMixin(StageType.DOCUMENT, [SET_STATUS_ACTION], getDocumentViewData), // musi być później, żeby nie nadpisała się metoda `afterStatusSave`
    previewMixin,
    signatureMixin
  ],
  data() {
    return {
      entityType: TableName.document
    };
  },
  computed: {
    ...mapState({
      document: state => state.document.data,
      documentIndex: state => state.documentIndex.data,
      withoutInfo: state => state.setting.settings[PublicSettings.PROCEDURE_WITHOUT_INFO]?.value === 'true',
      submittedManuallyText: state => state.setting.settings[PublicSettings.SUBMITTED_MANUALLY_TEXT]?.value || false,
      isSecretary: state => !!state.auth.nodes?.length,
      lock: state => state.lock.data,
      separateSchoolsDisplay: state => state.setting.settings[PublicSettings.SEPARATE_SCHOOLS_DISPLAY]?.value === 'true'
    }),
    mdokUid() {
      if (this.isSecretary) {
        return this.document.systemNumbers?.mdokJoId;
      }
      return this.document.systemNumbers?.mdokId;
    },
    parentId() {
      return this.$route.query.parentId;
    },
    messageName() {
      if (this.document.data && this.document.data.title) {
        return `Przypomnienie o wniosku: "${this.document.data.title}"`;
      }
      return `Przypomnienie o wniosku procedury: "${this.document.procedure.name}"`;
    },
    isSubmitted() {
      return !!this.documentIndex.values?.[DocumentValueCodes.SUBMIT_DATE];
    },
    isSubmittedManually() {
      return this.isSubmitted && !this.document.signedAttachment;
    },
    isDelivered() {
      return !!this.documentIndex.values?.[DocumentValueCodes.DELIVERY_DATE];
    },
    submitToSchool() {
      return this.documentIndex.data?.school?.financingType === 1;
    },
    applicantOrTenderer() {
      return this.documentIndex.data?.applicant?.fullName || this.documentIndex.data?.tenderer?.fullName;
    },
    studentFullName() {
      return (
        this.documentIndex.data?.employee?.fullName ||
        this.documentIndex.data?.tenderer?.fullName ||
        this.documentIndex.data?.student?.fullName
      );
    },
    downloadXmlLink() {
      return `${config.mediaURL}/document/${this.documentIndex.documentId}`;
    }
  },
  methods: {
    // @vuese
    // Przejście do MDOK
    goToMdok() {
      documentResource
        .getMdokUrlByUid(parseInt(this.mdokUid), this.document.id)
        .then(url => window.open(url, '_blank'))
        .catch(err => this.notifyError(Errors.MDOK_LINK_FAILED));
    },

    // @vuese
    // Przesłanie dokumentu przez epuap
    sendWithEpuap() {
      return epuapResource.getFormData({ documentId: this.document.id }).then(formData => {
        const formContainer = this.$refs.temporaryFormContainer;

        const form = document.createElement('form');
        form.action = formData.url;
        form.method = 'POST';
        form.target = '_blank';

        Object.keys(formData.data).forEach(key => {
          const input = document.createElement('textarea');
          input.name = key;
          input.value = formData.data[key];
          form.appendChild(input);
        });

        formContainer.appendChild(form);

        form.submit();
      });
    }
  },

  beforeRouteUpdate(to, from, next) {
    getDocumentViewData(to)
      .then(() => next())
      .catch(() => this.notifyError(Errors.DOCUMENT_CANNOT_GET));
  }
};
</script>

<style>
.document-view ul.dashed {
  padding-left: 5px;
  list-style-type: none;
}
.document-view ul.dashed > li {
  text-indent: -5px;
}
.document-view ul.dashed > li:before {
  content: '-';
  text-indent: -5px;
}

.lock-info {
  position: absolute;
  bottom: 0;
}
</style>
